import { AxiosRequestConfig } from 'axios';
import { HttpClient } from './http-client';
import {
  AML_SERVICE,
  CAPITAL_SERVICE,
  OPERATIONS_SERVICE,
  QUIN_ADAPTER_SERVICE,
  QUIN_CASE_SERVICE,
} from './services-path';

export type OperationsRequestContext = {
  tenantId?: string;
  tenantCode?: string;
  actorId?: string;
  actorRole?: string;
  actorRoles?: string[];
};

export type PageResponse<T> = {
  items: T[];
  total: number;
  pageIndex: number;
  pageSize: number;
};

export type AmlClusterEntity = {
  entityType?: string;
  entityId?: string;
  role?: string;
  contributionScore?: number;
};

export type AmlClusterSignal = {
  signalCode?: string;
  description?: string;
  confidence?: number;
  severity?: string;
  evidenceRefs?: string[];
};

export type AmlRestriction = {
  restrictionId?: string;
  caseId?: string;
  restrictionType?: string;
  status?: string;
  reason?: string;
  createdAt?: string;
  releasedAt?: string;
};

export type AmlSarSummary = {
  sarId?: string;
  caseId?: string;
  status?: string;
  draft?: Record<string, unknown>;
  approvedBy?: string;
  externalReference?: string;
  createdAt?: string;
  approvedAt?: string;
};

export type AmlCaseSummary = {
  caseId: string;
  clusterId: string;
  status?: string;
  severity?: string;
  assignedTo?: string;
  createdAt?: string;
  updatedAt?: string;
  closedAt?: string;
  clusterType?: string;
  deterministicScore?: number;
  detectedAt?: string;
  summary?: Record<string, unknown>;
  aiConfidence?: number;
  typology?: string;
  recommendedAction?: string;
  finalConfidence?: number;
  policyDecision?: string;
  entities?: AmlClusterEntity[];
  signals?: AmlClusterSignal[];
  restrictions?: AmlRestriction[];
  sarReports?: AmlSarSummary[];
};

export type AmlCaseDecision = {
  decision?: string;
  reasonCode?: string;
  comments?: string;
  actorId?: string;
  createdAt?: string;
};

export type AmlTimelineEvent = {
  eventId?: string;
  type?: string;
  label?: string;
  actor?: string;
  result?: string;
  createdAt?: string;
  metadata?: Record<string, unknown>;
};

export type AmlAiTraceEvent = {
  agentName?: string;
  eventType?: string;
  description?: string;
  inputPayload?: Record<string, unknown>;
  outputPayload?: Record<string, unknown>;
  confidenceScore?: number;
  decisionReason?: string;
  createdAt?: string;
};

export type AmlGraphNode = {
  id: string;
  label?: string;
  entityType?: string;
  role?: string;
  score?: number;
};

export type AmlGraphEdge = {
  id: string;
  from: string;
  to: string;
  relationshipType?: string;
  strength?: number;
  firstSeen?: string;
  lastSeen?: string;
  evidence?: Record<string, unknown>;
};

export type AmlGraphResponse = {
  nodes?: AmlGraphNode[];
  edges?: AmlGraphEdge[];
};

export type AmlCaseWorkspaceResponse = {
  caseItem?: AmlCaseSummary;
  decisions?: AmlCaseDecision[];
  timeline?: AmlTimelineEvent[];
  aiTrace?: AmlAiTraceEvent[];
  graph?: AmlGraphResponse;
};

export type AmlRuleConfig = {
  ruleId?: string;
  tenantId?: string;
  ruleCode: string;
  displayName: string;
  description?: string;
  category: string;
  enabled: boolean;
  approvalStatus: string;
  version: number;
  thresholds?: Record<string, unknown>;
  score: number;
  severity: string;
  action: string;
  assignedRole: string;
  slaHours: number;
  aiAssisted?: boolean;
  aiPatternScope?: string;
  createdBy?: string;
  createdAt?: string;
  submittedBy?: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  supersedesRuleId?: string;
};

export type OperationsDashboard = {
  openCasesByRole?: Record<string, number>;
  openCasesByStatus?: Record<string, number>;
  openCasesByPriority?: Record<string, number>;
  openTasksByRole?: Record<string, number>;
  openTasksByStatus?: Record<string, number>;
  openTasksByPriority?: Record<string, number>;
  priorityCases?: number;
  slaBreachedCases?: number;
  dueSoonCases?: number;
  priorityTasks?: number;
  overdueTasks?: number;
  readyReports?: number;
  openDocumentRequests?: number;
};

export type ClientDashboardPortfolioSummary = {
  currencyCode?: string;
  portfolioValue?: number;
  availableToTrade?: number;
};

export type ClientDashboardResponse = {
  partyId?: string;
  portfolio?: ClientDashboardPortfolioSummary[];
};

export type OperationCase = {
  createdAt?: string;
  caseId?: string;
  caseReference?: string;
  temporalWorkflowId?: string;
  temporalRunId?: string;
  caseType?: string;
  clientId?: string;
  accountId?: string;
  accountType?: string;
  customerName?: string;
  customerNumber?: string;
  tradeReference?: string;
  advisorId?: string;
  firmId?: string;
  title?: string;
  description?: string;
  priority?: string;
  status?: string;
  handlerType?: string;
  assignedRole?: string;
  assignedAgent?: string;
  assignedQueue?: string;
  assignedUserId?: string;
  channel?: string;
  intent?: string;
  riskLevel?: string;
  dueAt?: string;
  slaDueAt?: string;
  closedAt?: string;
  updatedAt?: string;
  sourceService?: string;
  sourceReferenceId?: string;
  complaintDetected?: boolean;
  regulatedAdviceDetected?: boolean;
  vulnerableCustomerDetected?: boolean;
  metadata?: Record<string, unknown>;
};

export type OperationAuditEvent = {
  auditId?: string;
  entityType?: string;
  entityId?: string;
  action?: string;
  actorId?: string;
  actorRole?: string;
  message?: string;
  createdAt?: string;
  metadata?: Record<string, unknown>;
};

export type OperationMessageAttachment = {
  source?: string;
  fileName?: string;
  mimeType?: string;
  createdAt?: string;
  messageId?: string;
  documentId?: string;
  attachmentId?: string;
  documentType?: string;
};

export type OperationMessage = {
  messageId?: string;
  caseId?: string;
  channel?: string;
  direction?: string;
  externalMessageId?: string;
  threadId?: string;
  sender?: string;
  recipient?: string;
  subject?: string;
  body?: string;
  receivedAt?: string;
  createdAt?: string;
  originalCustomerMessage?: boolean;
  attachments?: OperationMessageAttachment[];
};

export type ClientSearchAccount = {
  accountId?: string;
  accountReference?: string;
  externalAccountId?: string;
  accountType?: string;
  wrapperType?: string;
  status?: string;
  openedAt?: string;
  openedDate?: string;
  openingDate?: string;
  accountOpeningDate?: string;
  closedAt?: string;
  closedDate?: string;
  closingDate?: string;
  accountClosingDate?: string;
};

export type ClientSearchResult = {
  clientId?: string;
  partyId?: string;
  clientReference?: string;
  externalPartyId?: string;
  partyType?: string;
  fullName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  status?: string;
  kycStatus?: string;
  kycVerified?: boolean;
  kycDocumentsRequired?: boolean;
  primaryAccountId?: string;
  primaryAccountReference?: string;
  matchedOn?: string;
  accounts?: ClientSearchAccount[];
};

export type EmailItem = {
  message?: OperationMessage;
  caseItem?: OperationCase;
};

export type CaseNoteResponse = {
  noteId: string;
  noteType: string;
  body: string;
  authorId: string;
  authorRole: string;
  createdAt: string;
  updatedAt?: string;
  pinned?: boolean;
};

export type CaseWorkflowEvent = {
  eventId?: string;
  status?: string;
  action?: string;
  label?: string;
  message?: string;
  actorId?: string;
  actorRole?: string;
  createdAt?: string;
  metadata?: Record<string, unknown>;
};

export type OperationTask = {
  taskId?: string;
  caseId?: string;
  taskType?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  assignedRole?: string;
  assignedUserId?: string;
  dueAt?: string;
  completedAt?: string;
  metadata?: Record<string, unknown>;
};

export type ClientServiceRequest = {
  requestId?: string;
  caseId?: string;
  clientId?: string;
  accountId?: string;
  requestType?: string;
  channel?: string;
  subject?: string;
  description?: string;
  status?: string;
  assignedRole?: string;
  dueAt?: string;
  metadata?: Record<string, unknown>;
};

export type DocumentRequest = {
  documentRequestId?: string;
  caseId?: string;
  clientId?: string;
  requestedDocumentType?: string;
  reason?: string;
  status?: string;
  requestedBy?: string;
  dueAt?: string;
  receivedDocumentId?: string;
  metadata?: Record<string, unknown>;
};

export type MandateRecord = Record<string, unknown>;
export type BankAccountRecord = Record<string, unknown>;

export type CustomerProfileFlags = {
  customerFlagsId?: string;
  tenantId?: string;
  clientId?: string;
  vulnerableCustomer?: boolean;
  vip?: boolean;
  goneAway?: boolean;
  staff?: boolean;
  crownEmployeeOrArmedForces?: boolean;
  reason?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  metadata?: Record<string, unknown>;
};

export type ClientCareContextResponse = {
  lookup?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  clientMatches?: ClientSearchResult[];
  clientSnapshot?: Record<string, unknown>;
  customerFlags?: CustomerProfileFlags;
  holdings?: AccountHoldingsView[];
  cases?: OperationCase[];
  serviceRequests?: ClientServiceRequest[];
  documentRequests?: DocumentRequest[];
};

export type AccountRestrictionPayload = {
  clientId: string;
  accountId: string;
  accountReference?: string;
  restrictions: string[];
  restrictionState?: Record<string, unknown>;
  reason?: string;
  caseId?: string;
  applyToSubAccounts?: boolean;
  actor?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

export type AccountRestrictionFlagsResponse = {
  flags?: string[];
  reason?: string | null;
};

export type HoldingView = {
  isin?: string;
  fundName?: string;
  status?: string;
  settledUnits?: number | string | null;
  settledPrice?: number | string | null;
  totalValue?: number | string | null;
};

export type AccountHoldingsView = {
  accountId: string;
  items: HoldingView[];
};

export type AccountRestrictionResponse = {
  id?: string;
  createdAt?: string;
  updatedAt?: string;
  partyId?: string;
  externalAccountId?: string | null;
  portfolioId?: string | null;
  accountReference?: string;
  accountType?: string;
  wrapperType?: string;
  consent?: boolean;
  status?: string;
  previousStatus?: string;
  restrictionFlags?: AccountRestrictionFlagsResponse;
  restrictionReason?: string | null;
  restrictionSource?: string;
  restrictionCaseId?: string | null;
  closureRequestDate?: string | null;
  closedDate?: string | null;
  openedAt?: string;
};

export type DeceasedCustomerWorkflow = {
  workflowId?: string;
  tenantId?: string;
  caseId?: string;
  clientId?: string;
  customerName?: string;
  customerNumber?: string;
  status?: string;
  dateOfDeath?: string;
  reportedBy?: string;
  reporterRelationship?: string;
  reportSource?: string;
  authorityType?: string;
  authorityName?: string;
  authorityContact?: string;
  poaProvided?: boolean;
  poaValidAfterDeath?: boolean;
  evidenceStatus?: string;
  settlementStatus?: string;
  closedAt?: string;
  affectedAccountIds?: string[];
  documentRequestIds?: string[];
  restrictionEventIds?: string[];
  stageHistory?: Record<string, unknown>[];
  metadata?: Record<string, unknown>;
};

export type CaseWorkspaceResponse = {
  caseItem?: OperationCase;
  notes?: CaseNoteResponse[];
  workflow?: CaseWorkflowEvent[];
  tasks?: OperationTask[];
  messages?: OperationMessage[];
  documentRequests?: DocumentRequest[];
  auditEvents?: OperationAuditEvent[];
  deceasedCustomerWorkflow?: DeceasedCustomerWorkflow;
};

export type CaseAiTraceEvent = {
  aiTraceEventId?: string;
  tenantId?: string;
  caseId?: string;
  workflowId?: string;
  runId?: string;
  agentName?: string | null;
  eventType?: string;
  description?: string;
  inputPayload?: Record<string, unknown> | null;
  outputPayload?: Record<string, unknown> | null;
  confidenceScore?: number | null;
  decisionReason?: string | null;
  createdAt?: string;
};

export type CaseAiTraceResponse = {
  caseId?: string;
  originalMessage?: OperationMessage;
  timeline?: CaseAiTraceEvent[];
  events?: CaseAiTraceEvent[];
  businessExceptions?: Record<string, unknown>[];
  technicalExceptions?: Record<string, unknown>[];
};

export type TenantUser = {
  userId?: string;
  cognitoUsername?: string;
  username?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  status?: string;
  roleCodes?: string[];
  metadata?: Record<string, unknown>;
};

export type TenantRole = {
  roleCode?: string;
  roleName?: string;
  displayName?: string;
  description?: string;
  permissions?: string[];
  systemRole?: boolean;
};

export type CertificateSettings = {
  enabled: boolean;
  companyName?: string;
  companyNumber?: string;
  legalStructure?: string;
  title?: string;
  shareClass?: string;
  nominalValue?: number | null;
  numberPrefix?: string;
  accentColor?: string;
  logo?: string;
  verificationBaseUrl?: string;
  signatoryOneName?: string;
  signatoryOneRole?: string;
  signatoryOneSignature?: string;
  signatoryTwoName?: string;
  signatoryTwoRole?: string;
  signatoryTwoSignature?: string;
  holdingWording?: string;
  terms?: string;
};
export type CommitmentCertificateStatus = {
  certificate?: ShareCertificate;
  automaticEnabled: boolean;
  settingsReady: boolean;
  allocated: boolean;
  canIssue: boolean;
  canPreview: boolean;
  reason: string;
};
export type ShareCertificate = {
  id: string;
  certificateNumber: string;
  companyName: string;
  fundName: string;
  replacedByCertificateId?: string;
  holderName?: string;
  status: string;
  issuedAt: string;
  attempts: number;
  lastError?: string;
};

export type CapitalSpv = {
  id?: string;
  spvName?: string;
  spvCode?: string;
  legalStructure?: string;
  jurisdictionCode?: string;
  issuerName?: string;
  companyInformation?: string;
  raiseInformation?: string;
  issuePricePerUnit?: number;
  status?: string;
  vintageYear?: number;
  targetCloseDate?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CapitalFundCampaign = {
  retailHeadline?: string;
  retailUpdates?: string;
  riskWarning?: string;
  exampleCommitmentAmount?: number;
  paymentMethod?: string;
  openBankingThresholdAmount?: number;
  paymentPayee?: string;
  paymentBankName?: string;
  paymentSortCode?: string;
  paymentAccountNumber?: string;
  paymentWindowDays?: number;
  allocationMode?: string;
  supportingDocuments?: Array<{
    id?: string;
    title?: string;
    version?: string;
    byteSize?: number;
    documentType?: string;
    summary?: string;
    fileName?: string;
    mimeType?: string;
    bucketKey?: string;
    displayOrder?: number;
  }>;
  id?: string;
  spvId?: string;
  fundName?: string;
  slug?: string;
  description?: string;
  about?: string;
  investmentHighlights?: string;
  investorEligibility?: string;
  investorTypeTags?: string[];
  qualificationCriteria?: string[];
  restrictedJurisdictions?: string[];
  liquidityTerms?: string;
  liquidityProfile?: string;
  distributionPolicy?: string;
  distributionFrequency?: string;
  riskSummary?: string;
  riskLevel?: string;
  tickerSymbol?: string;
  exchangeCode?: string;
  sector?: string;
  raiseType?: string;
  instrumentName?: string;
  issuePricePerUnit?: number;
  sharesOnIssuePostRaise?: number;
  settlementBasis?: string;
  useOfProceeds?: string;
  useOfProceedsTags?: string[];
  companyOverview?: string;
  investmentCaseContent?: string;
  financialsContent?: string;
  keyTermsContent?: string;
  companyContent?: string;
  brokerName?: string;
  brokerTitle?: string;
  brokerPhone?: string;
  brokerEmail?: string;
  assetClass?: string;
  currencyCode?: string;
  status?: string;
  opensAt?: string;
  closesAt?: string;
  targetRaiseAmount?: number;
  minimumCommitmentAmount?: number;
  maximumCommitmentAmount?: number;
  unitsCreatedForRound?: number;
  amountCommitted?: number;
  amountSettled?: number;
  amountRefunded?: number;
  hardCapEnforced?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type CapitalCommitment = {
  paymentMethod?: string;
  openBankingThresholdAmount?: number;
  id?: string;
  investorPartyId?: string;
  investorAccountId?: string;
  investorSubAccountId?: string;
  fundCampaignId?: string;
  commitmentReference?: string;
  status?: string;
  approvalStatus?: string;
  paymentStatus?: string;
  withdrawalStatus?: string;
  committedAmount?: number;
  settledAmount?: number;
  refundedAmount?: number;
  refundableAmount?: number;
  withdrawalRequestedAmount?: number;
  paymentReference?: string;
  paymentTransactionReference?: string;
  withdrawalReference?: string;
  registerStatus?: string;
  secureMessageSubject?: string;
  secureMessageBody?: string;
  committedAt?: string;
  approvedAt?: string;
  paymentRequestedAt?: string;
  paymentReceivedAt?: string;
  paymentApprovedAt?: string;
  paymentApprovedBy?: string;
  withdrawalRequestedAt?: string;
  withdrawalRejectedAt?: string;
  withdrawalApprovedAt?: string;
  withdrawalPaidAt?: string;
  coolingOffEndsAt?: string;
  withdrawalRequestedBy?: string;
  withdrawalRequestReason?: string;
  withdrawalRejectedReason?: string;
  withdrawalApprovedBy?: string;
  withdrawalEnabledByOperations?: boolean;
  withdrawalEnabledAt?: string;
  withdrawalEnabledBy?: string;
  canWithdraw?: boolean;
  notes?: string;
  investor?: {
    partyId?: string;
    clientReference?: string;
    name?: string;
    status?: string;
    kycStatus?: string;
    kycVerified?: boolean;
    kycDocumentsRequired?: boolean;
  };
  account?: {
    accountId?: string;
    accountReference?: string;
    accountType?: string;
    accountName?: string;
    accountStatus?: string;
  };
  contact?: {
    email?: string;
    phoneNumber?: string;
  };
  address?: {
    addressType?: string;
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    countryCode?: string;
  };
  payoutBankAccount?: {
    accountHolderName?: string;
    bankName?: string;
    sortCode?: string;
    accountNumberMasked?: string;
    currencyCode?: string;
    status?: string;
    verificationStatus?: string;
  };
  kyc?: {
    kycStatus?: string;
    kycVerified?: boolean;
    kycDocumentsRequired?: boolean;
    onboardingProcessing?: boolean;
    kycChecksStartedAt?: string;
    kycCompletedAt?: string;
  };
  documents?: Array<{
    id?: string;
    documentType?: string;
    fileName?: string;
    mimeType?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
  }>;
};

export type CapitalRegisterEntry = {
  id?: string;
  fundCampaignId?: string;
  commitmentId?: string;
  investorPartyId?: string;
  privateEquityAccountId?: string;
  registerReference?: string;
  unitsAllocated?: number;
  unitsReversed?: number;
  amountReversed?: number;
  pricePerUnit?: number;
  amountRegistered?: number;
  entryStatus?: string;
  registeredAt?: string;
  completedAt?: string;
  notes?: string;
};

export type CapitalFundValuation = {
  id?: string;
  fundCampaignId?: string;
  spvId?: string;
  valuationDate?: string;
  unitSharePrice?: number;
  currencyCode?: string;
  notes?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type ActionResponse = {
  message?: string;
};

export type CapitalRaiseMessageDraftResponse = {
  subject?: string;
  body?: string;
  draftSource?: string;
};

export type RegulatoryReportRun = {
  runId?: string;
  reportType?: string;
  periodStart?: string;
  periodEnd?: string;
  status?: string;
  generatedBy?: string;
  approvedBy?: string;
  exportedBy?: string;
  failureReason?: string;
  metrics?: Record<string, unknown>;
};

export type ReconciliationRun = {
  runId?: string;
  workflowId?: string;
  businessDate?: string;
  overallStatus?: string;
  matchedCount?: number;
  breakCount?: number;
  reportLocation?: string;
  reportStatus?: string;
  reportAvailable?: boolean;
  failureReason?: string | null;
  reportFailureReason?: string | null;
  createdAt?: string;
  completedAt?: string;
  startedAt?: string;
  endedAt?: string;
};

export type ReconciliationMismatch = Record<string, unknown>;

export type PartyReconciliationMismatch = Record<string, unknown> & {
  id?: string;
  runId?: string;
  businessDate?: string;
  partyId?: string;
  wkPartyId?: string;
  clientReference?: string;
  issueType?: string;
  details?: string;
};

export type ReconciliationReportDownload = {
  runId?: string;
  fileName?: string;
  mimeType?: string;
  downloadUrl?: string;
  expiresAt?: string;
  generatedAt?: string;
};

export type RegulatoryReportDefinition = {
  definitionId?: string;
  reportType?: string;
  displayName?: string;
  ownerRole?: string;
  frequency?: string;
  enabled?: boolean;
  sourceServices?: string[];
};

export type OpsLoginPayload = {
  username: string;
  password: string;
};

export type OpsPasswordResetPayload = {
  username: string;
  oldPassword: string;
  newPassword: string;
};

export type TenantUserPasswordResetPayload = {
  username?: string;
  temporaryPassword: string;
  requirePasswordChange?: boolean;
  actor?: Record<string, unknown>;
};

export type QuinSearchPayload = {
  pageNo?: number;
  recordsPerPage?: number;
  pageIndex?: number;
  pageSize?: number;
  sort?: Record<string, unknown>;
  sortBy?: string;
  sortDirection?: string;
  searchText?: string;
  searchParams?: Record<string, unknown>;
  filters?: Record<string, unknown>;
  fields?: Record<string, unknown>[];
  [key: string]: unknown;
};

export type QkvinCaseResponse = {
  clientId?: string;
  orgId?: string;
  caseId?: string;
  tenantId?: string;
  clientType?: string;
  caseType?: string;
  caseStatus?: string;
  caseSubStatus?: string;
  caseNumber?: string;
  createdDate?: string;
  updatedDate?: string;
  createdByName?: string;
  updatedByName?: string;
  dueDate?: string;
  assignedCaseWorkerName?: string;
  caseEditSupervisorName?: string;
  caseEditSupervisorDecision?: string;
  commentHistories?: Record<string, unknown>[];
  orgName?: string;
  overAllRisk?: string;
  kycStatus?: string;
  dueDiligenceType?: string;
  jurisdiction?: string;
  clientName?: string;
  clientNumber?: string;
  clientDetailsId?: string;
  isReviewed?: boolean;
  reviewHistories?: Record<string, unknown>[];
  dateOfBirth?: string;
  incorporatedOn?: string;
  country?: string;
  type?: string;
  authorizerId?: string;
  authorizerName?: string;
  authorisedTime?: string;
  [key: string]: unknown;
};

export type QkvinWorkflowActivity = {
  activityName?: string;
  activityLabel?: string;
  activityNumber?: number;
  referenceId?: string;
  clientId?: string;
  activityType?: string;
  activityStatus?: string;
  activityId?: string;
  activityDetails?: Record<string, unknown>;
  stageName?: string;
  activityStartDatetime?: string;
  activityEndDatetime?: string;
  assignedToName?: string;
  enabled?: boolean;
  actionRequired?: boolean;
  editable?: boolean | null;
  activitySubStatus?: string;
  entityId?: string | null;
  dependedActivities?: string[] | null;
  newInformationAvailable?: boolean;
};

export type QkvinWorkflowStage = {
  stageId?: string;
  stageNumber?: number;
  stageName?: string;
  stageLabel?: string;
  sla?: number | null;
  runCustomActivities?: boolean;
  customActivityName?: string | null;
  activities?: QkvinWorkflowActivity[];
  stageStatus?: string;
  createdAt?: string;
  updatedAt?: string;
  endedAt?: string | null;
  totalCompletedActivity?: number;
  totalEnabledActivities?: number;
  totalDuration?: number;
  durationInDays?: number;
};

export type QkvinWorkflowResponse = {
  clientId?: string;
  caseId?: string;
  stages?: QkvinWorkflowStage[];
  keyIndividualsIds?: string[];
};

export type QkvinCheckDetailResponse = {
  checkId?: string;
  checkName?: string;
  checkLabel?: string;
  verificationType?: string;
  verificationSources?: string[];
  threshold?: string;
  matchPercentage?: string;
  status?: string;
  riskLevel?: string;
  updatedDate?: string;
  groupedRiskLevel?: string;
  groupedStatus?: string;
  groupedKycStatus?: string | null;
  mediumRiskThreshold?: string;
  highRiskThreshold?: string;
  kycStatusThreshold?: string;
  kycStatus?: string;
  lastChecKUpdateLog?: Record<string, unknown> | null;
  override?: boolean;
  overrideRiskRequestDTOS?: Record<string, unknown>[] | null;
  customCheckDetails?: Record<string, unknown> | null;
  documentReview?: Record<string, unknown> | null;
  reTriggerEnabled?: boolean;
  overRideEnabled?: boolean;
  clientId?: string;
  caseId?: string;
  clientName?: string;
  clientNumber?: string;
  clientType?: string;
  caseNumber?: string;
  orgName?: string;
  policyId?: string;
  policyName?: string;
  [key: string]: unknown;
};

export type QkvinOverallScreeningResult = {
  kycStatus?: string | null;
  automatedKycStatus?: string | null;
  clientType?: string | null;
  overAllRiskProfile?: string | null;
  updateByName?: string | null;
  userRole?: string | null;
  [key: string]: unknown;
};

export type QkvinKycAnalystReviewResponse = {
  clientId?: string;
  tenantId?: string;
  authStatus?: string;
  caseSubStatus?: string;
  [key: string]: unknown;
};

export type OpsRefreshSessionPayload = {
  token: string;
};

const requestConfig = (
  context: OperationsRequestContext = {},
  config: AxiosRequestConfig = {},
): AxiosRequestConfig => ({
  ...config,
  headers: {
    ...(context.tenantId ? { 'X-Tenant-Id': context.tenantId } : {}),
    ...(context.tenantCode ? { 'X-Tenant-Code': context.tenantCode } : {}),
    ...(context.actorId ? { 'X-Actor-User-Id': context.actorId } : {}),
    ...(context.actorRole ? { 'X-Actor-Role': context.actorRole } : {}),
    ...(context.actorRoles?.length
      ? { 'X-Actor-Roles': context.actorRoles.join(',') }
      : {}),
    ...config.headers,
  },
});

const noCacheRequestConfig = (
  context: OperationsRequestContext = {},
  config: AxiosRequestConfig = {},
): AxiosRequestConfig =>
  requestConfig(context, {
    ...config,
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
      ...(config.headers ?? {}),
    },
  });

export const OperationsServiceAPIs = () => {
  return {
    login: (
      payload: OpsLoginPayload,
      loginPath = `${OPERATIONS_SERVICE}/auth/login`,
    ) =>
      HttpClient.post(
        loginPath,
        { username: payload.username, password: payload.password },
        undefined,
        true,
      ),

    refreshSession: (
      payload: OpsRefreshSessionPayload,
      refreshPath = `${OPERATIONS_SERVICE}/auth/refresh`,
    ) => HttpClient.post(refreshPath, payload),

    resetPassword: (
      payload: OpsPasswordResetPayload,
      resetPasswordPath = `${OPERATIONS_SERVICE}/auth/password/reset`,
    ) =>
      HttpClient.post(
        resetPasswordPath,
        {
          username: payload.username,
          oldPassword: payload.oldPassword,
          newPassword: payload.newPassword,
        },
        undefined,
        true,
      ),

    dashboard: (context: OperationsRequestContext) =>
      HttpClient.get<OperationsDashboard>(
        `${OPERATIONS_SERVICE}/dashboard`,
        requestConfig(context),
      ),

    dashboardOpenCases: (
      context: OperationsRequestContext,
      pageIndex = 0,
      pageSize = 25,
    ) =>
      HttpClient.get<PageResponse<OperationCase>>(
        `${OPERATIONS_SERVICE}/dashboard/open-cases`,
        requestConfig(context, { params: { pageIndex, pageSize } }),
      ),

    clientDashboard: (context: OperationsRequestContext, partyId: string) =>
      HttpClient.get<ClientDashboardResponse>(
        `${OPERATIONS_SERVICE}/clients/${partyId}/dashboard`,
        requestConfig(context),
      ),

    updateClientContactDetails: (
      context: OperationsRequestContext,
      clientId: string,
      payload: { phoneNumber: string; email: string },
    ) =>
      HttpClient.patch(
        `${OPERATIONS_SERVICE}/clients/${clientId}/contact-details`,
        payload,
        requestConfig(context),
      ),

    roles: (context: OperationsRequestContext) =>
      HttpClient.get<TenantRole[]>(
        `${OPERATIONS_SERVICE}/roles`,
        requestConfig(context),
      ),

    commitmentCertificateStatus: (
      context: OperationsRequestContext,
      commitmentId: string,
    ) =>
      HttpClient.get<CommitmentCertificateStatus>(
        `${CAPITAL_SERVICE}/certificates/commitments/${commitmentId}`,
        requestConfig(context),
      ),
    issueCommitmentCertificate: (
      context: OperationsRequestContext,
      commitmentId: string,
    ) =>
      HttpClient.post<ShareCertificate>(
        `${CAPITAL_SERVICE}/certificates/commitments/${commitmentId}/issue`,
        {},
        requestConfig(context),
      ),
    previewCommitmentCertificate: (
      context: OperationsRequestContext,
      commitmentId: string,
    ) =>
      HttpClient.get<Blob>(
        `${CAPITAL_SERVICE}/certificates/commitments/${commitmentId}/preview`,
        { ...requestConfig(context), responseType: 'blob' },
      ),
    certificateSettings: (context: OperationsRequestContext, spvId: string) =>
      HttpClient.get<CertificateSettings>(
        `${CAPITAL_SERVICE}/certificates/issuers/${spvId}/settings`,
        requestConfig(context),
      ),
    saveCertificateSettings: (
      context: OperationsRequestContext,
      spvId: string,
      payload: CertificateSettings,
    ) =>
      HttpClient.put<CertificateSettings>(
        `${CAPITAL_SERVICE}/certificates/issuers/${spvId}/settings`,
        payload,
        requestConfig(context),
      ),
    previewCertificate: (
      context: OperationsRequestContext,
      spvId: string,
      payload: CertificateSettings,
    ) =>
      HttpClient.post<Blob>(
        `${CAPITAL_SERVICE}/certificates/issuers/${spvId}/preview`,
        payload,
        { ...requestConfig(context), responseType: 'blob' },
      ),
    campaignCertificates: (
      context: OperationsRequestContext,
      campaignId: string,
    ) =>
      HttpClient.get<ShareCertificate[]>(
        `${CAPITAL_SERVICE}/certificates/campaigns/${campaignId}`,
        requestConfig(context),
      ),
    issueExistingCertificates: (
      context: OperationsRequestContext,
      campaignId: string,
    ) =>
      HttpClient.post(
        `${CAPITAL_SERVICE}/certificates/campaigns/${campaignId}/issue-existing`,
        {},
        requestConfig(context),
      ),
    replaceCertificate: (
      context: OperationsRequestContext,
      id: string,
      reason: string,
    ) =>
      HttpClient.post(
        `${CAPITAL_SERVICE}/certificates/${id}/replace`,
        { reason },
        requestConfig(context),
      ),
    retryCertificate: (context: OperationsRequestContext, id: string) =>
      HttpClient.post(
        `${CAPITAL_SERVICE}/certificates/${id}/retry`,
        {},
        requestConfig(context),
      ),
    downloadCertificate: (context: OperationsRequestContext, id: string) =>
      HttpClient.get<Blob>(`${CAPITAL_SERVICE}/certificates/${id}/download`, {
        ...requestConfig(context),
        responseType: 'blob',
      }),

    capitalRaiseSpvs: (context: OperationsRequestContext, tenantId: string) =>
      HttpClient.get<CapitalSpv[]>(
        `${CAPITAL_SERVICE}/spvs`,
        requestConfig(context),
      ),

    createCapitalRaiseSpv: (
      context: OperationsRequestContext,
      tenantId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<CapitalSpv>(
        `${CAPITAL_SERVICE}/spvs`,
        payload,
        requestConfig(context),
      ),

    updateCapitalRaiseSpv: (
      context: OperationsRequestContext,
      tenantId: string,
      spvId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalSpv>(
        `${CAPITAL_SERVICE}/spvs/${spvId}`,
        payload,
        requestConfig(context),
      ),

    capitalRaiseFundCampaigns: (
      context: OperationsRequestContext,
      tenantId: string,
    ) =>
      HttpClient.get<CapitalFundCampaign[]>(
        `${CAPITAL_SERVICE}/fund-campaigns`,
        requestConfig(context),
      ),

    capitalRaiseCommitments: (
      context: OperationsRequestContext,
      tenantId: string,
    ) =>
      HttpClient.get<CapitalCommitment[]>(
        `${CAPITAL_SERVICE}/commitments`,
        requestConfig(context),
      ),

    allocateCapitalRaiseCommitment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/allocate`,
        {},
        requestConfig(context),
      ),

    capitalRaiseRegisterEntries: (
      context: OperationsRequestContext,
      tenantId: string,
    ) =>
      HttpClient.get<CapitalRegisterEntry[]>(
        `${CAPITAL_SERVICE}/register-entries`,
        requestConfig(context),
      ),

    capitalRaiseFundValuations: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
    ) =>
      HttpClient.get<CapitalFundValuation[]>(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/valuations`,
        requestConfig(context),
      ),

    createCapitalRaiseFundCampaign: (
      context: OperationsRequestContext,
      tenantId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<CapitalFundCampaign>(
        `${CAPITAL_SERVICE}/fund-campaigns`,
        payload,
        requestConfig(context),
      ),

    updateCapitalRaiseFundCampaign: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalFundCampaign>(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}`,
        payload,
        requestConfig(context),
      ),

    createCapitalRaiseFundValuation: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<CapitalFundValuation>(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/valuations`,
        payload,
        requestConfig(context),
      ),

    uploadCapitalRaiseFundCampaignDocument: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<
        CapitalFundCampaign['supportingDocuments'] extends Array<infer T>
          ? T
          : Record<string, unknown>
      >(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/supporting-documents`,
        payload,
        requestConfig(context),
      ),

    downloadCapitalRaiseFundCampaignDocument: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
      documentId: string,
    ) =>
      HttpClient.getDocument(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/supporting-documents/${documentId}/download`,
        requestConfig(context),
      ),

    openCapitalRaiseFundCampaign: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
    ) =>
      HttpClient.patch<CapitalFundCampaign>(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/open`,
        {},
        requestConfig(context),
      ),

    closeCapitalRaiseFundCampaign: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
    ) =>
      HttpClient.patch<CapitalFundCampaign>(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/close`,
        {},
        requestConfig(context),
      ),

    pauseCapitalRaiseFundCampaign: (
      context: OperationsRequestContext,
      tenantId: string,
      fundCampaignId: string,
    ) =>
      HttpClient.patch<CapitalFundCampaign>(
        `${CAPITAL_SERVICE}/fund-campaigns/${fundCampaignId}/pause`,
        {},
        requestConfig(context),
      ),

    approveCapitalRaiseCommitment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/approve`,
        payload,
        requestConfig(context),
      ),

    rejectCapitalRaiseCommitment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/reject`,
        payload,
        requestConfig(context),
      ),

    refundCapitalRaiseCommitment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/refund`,
        payload,
        requestConfig(context),
      ),

    requestCapitalRaiseCommitmentWithdrawal: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/withdrawal-request`,
        payload,
        requestConfig(context),
      ),

    rejectCapitalRaiseCommitmentWithdrawal: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/withdrawal-request/reject`,
        payload,
        requestConfig(context),
      ),

    setCapitalRaiseCommitmentWithdrawalAvailability: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/withdrawal-availability`,
        payload,
        requestConfig(context),
      ),

    reviewCapitalRaiseCommitment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<ActionResponse>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/review`,
        payload,
        requestConfig(context),
      ),

    recordCapitalRaiseCommitmentPayment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/payments/record`,
        {
          transactionReference:
            payload.paymentTransactionReference ?? payload.transactionReference,
          amount: payload.paymentAmount ?? payload.amount,
          actor: payload.actor,
        },
        requestConfig(context),
      ),

    manualSettleCapitalRaiseCommitment: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CapitalCommitment>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/manual-settlement`,
        {
          transactionReference:
            payload.paymentTransactionReference ?? payload.transactionReference,
          amount: payload.paymentAmount ?? payload.amount,
          reason: payload.reason,
          actor: payload.actor,
        },
        requestConfig(context),
      ),

    sendCapitalRaiseCommitmentMessage: (
      context: OperationsRequestContext,
      tenantId: string,
      commitmentId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<ActionResponse>(
        `${CAPITAL_SERVICE}/commitments/${commitmentId}/secure-message`,
        payload,
        requestConfig(context),
      ),

    generateCapitalRaiseMessageDraft: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<CapitalRaiseMessageDraftResponse>(
        `${OPERATIONS_SERVICE}/capital/message-drafts`,
        payload,
        requestConfig(context),
      ),

    tenantRoles: (context: OperationsRequestContext, tenantId: string) =>
      HttpClient.get<TenantRole[]>(
        `${OPERATIONS_SERVICE}/tenants/${tenantId}/roles`,
        requestConfig(context),
      ),

    tenantUsers: (
      context: OperationsRequestContext,
      tenantId: string,
      pageIndex = 0,
      pageSize = 50,
    ) =>
      HttpClient.get<PageResponse<TenantUser>>(
        `${OPERATIONS_SERVICE}/tenants/${tenantId}/users`,
        requestConfig(context, { params: { pageIndex, pageSize } }),
      ),

    createTenantUser: (
      context: OperationsRequestContext,
      tenantId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<TenantUser>(
        `${OPERATIONS_SERVICE}/tenants/${tenantId}/users`,
        payload,
        requestConfig(context),
      ),

    updateTenantUserStatus: (
      context: OperationsRequestContext,
      tenantId: string,
      userId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<TenantUser>(
        `${OPERATIONS_SERVICE}/tenants/${tenantId}/users/${userId}/status`,
        payload,
        requestConfig(context),
      ),

    assignTenantUserRoles: (
      context: OperationsRequestContext,
      tenantId: string,
      userId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put<TenantUser>(
        `${OPERATIONS_SERVICE}/tenants/${tenantId}/users/${userId}/roles`,
        payload,
        requestConfig(context),
      ),

    resetTenantUserPassword: (
      context: OperationsRequestContext,
      tenantId: string,
      userId: string,
      payload: TenantUserPasswordResetPayload,
    ) =>
      HttpClient.post<TenantUser>(
        `${OPERATIONS_SERVICE}/tenants/${tenantId}/users/${userId}/password/reset`,
        payload,
        requestConfig(context),
      ),

    cases: (
      context: OperationsRequestContext,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<PageResponse<OperationCase>>(
        `${OPERATIONS_SERVICE}/cases`,
        requestConfig(context, { params }),
      ),

    getCase: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<OperationCase>(
        `${OPERATIONS_SERVICE}/cases/${caseId}`,
        requestConfig(context),
      ),

    caseWorkspace: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<CaseWorkspaceResponse>(
        `${OPERATIONS_SERVICE}/cases/${caseId}/workspace`,
        requestConfig(context),
      ),

    caseNotes: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<CaseNoteResponse[]>(
        `${OPERATIONS_SERVICE}/cases/${caseId}/notes`,
        requestConfig(context),
      ),

    addCaseNote: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<CaseNoteResponse>(
        `${OPERATIONS_SERVICE}/cases/${caseId}/notes`,
        payload,
        requestConfig(context),
      ),

    updateCaseNote: (
      context: OperationsRequestContext,
      caseId: string,
      noteId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CaseNoteResponse>(
        `${OPERATIONS_SERVICE}/cases/${caseId}/notes/${noteId}`,
        payload,
        requestConfig(context),
      ),

    createCase: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationCase>(
        `${OPERATIONS_SERVICE}/cases`,
        payload,
        requestConfig(context),
      ),

    createAccountClosureCase: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationCase>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/account-closures`,
        payload,
        requestConfig(context),
      ),

    planAccountServicing: (context: OperationsRequestContext, caseId: string, accountId: string, payload: Record<string, unknown>) =>
      HttpClient.post<Record<string, unknown>>(`${OPERATIONS_SERVICE}/account-servicing/${caseId}/accounts/${accountId}/plan`, payload, requestConfig(context)),

    decideStoppedTransfer: (context: OperationsRequestContext, caseId: string, payload: { decisionId: string; note: string }) =>
      HttpClient.post<OperationCase>(`${OPERATIONS_SERVICE}/account-servicing/${caseId}/stopped-transfer-decision`, payload, requestConfig(context)),

    reconcileServicingProviderStatus: (context: OperationsRequestContext, caseId: string, payload: { decisionId: string; providerStatus: string; note: string }) =>
      HttpClient.post<OperationCase>(`${OPERATIONS_SERVICE}/account-servicing/${caseId}/reconcile-provider-status`, payload, requestConfig(context)),

    retryStoppedClosure: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.post<OperationCase>(`${OPERATIONS_SERVICE}/account-servicing/${caseId}/retry-stopped-closure`, {}, requestConfig(context)),

    revalidateAccountServicing: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.post<OperationCase>(`${OPERATIONS_SERVICE}/account-servicing/${caseId}/revalidate`, {}, requestConfig(context)),

    retryServicingOrder: (context: OperationsRequestContext, caseId: string, accountId: string, instructionId: string, orderId: string) =>
      HttpClient.post<Record<string, unknown>>(`${OPERATIONS_SERVICE}/account-servicing/${caseId}/accounts/${accountId}/instructions/${instructionId}/orders/${orderId}/retry`, {}, requestConfig(context)),


    updateCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<OperationCase>(
        `${OPERATIONS_SERVICE}/cases/${caseId}`,
        payload,
        requestConfig(context),
      ),

    assignCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationCase>(
        `${OPERATIONS_SERVICE}/api/cases/${caseId}/assign`,
        payload,
        requestConfig(context),
      ),

    submitCaseHumanResponse: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationCase>(
        `${OPERATIONS_SERVICE}/api/cases/${caseId}/human-response`,
        payload,
        requestConfig(context),
      ),

    closeCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationCase>(
        `${OPERATIONS_SERVICE}/api/cases/${caseId}/close`,
        payload,
        requestConfig(context),
      ),

    reopenCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationCase>(
        `${OPERATIONS_SERVICE}/api/cases/${caseId}/reopen`,
        payload,
        requestConfig(context),
      ),

    caseAiTrace: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<CaseAiTraceResponse>(
        `${OPERATIONS_SERVICE}/api/cases/${caseId}/ai-trace`,
        requestConfig(context),
      ),

    audit: (
      context: OperationsRequestContext,
      entityType: string,
      entityId: string,
    ) =>
      HttpClient.get<OperationAuditEvent[]>(
        `${OPERATIONS_SERVICE}/audit/${entityType}/${entityId}`,
        requestConfig(context),
      ),

    tasks: (
      context: OperationsRequestContext,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<PageResponse<OperationTask>>(
        `${OPERATIONS_SERVICE}/tasks`,
        requestConfig(context, { params }),
      ),

    completeTask: (
      context: OperationsRequestContext,
      taskId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationTask>(
        `${OPERATIONS_SERVICE}/tasks/${taskId}/complete`,
        payload,
        requestConfig(context),
      ),

    reportDefinitions: (context: OperationsRequestContext) =>
      HttpClient.get<RegulatoryReportDefinition[]>(
        `${OPERATIONS_SERVICE}/reports/definitions`,
        requestConfig(context),
      ),

    createReportRun: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<RegulatoryReportRun>(
        `${OPERATIONS_SERVICE}/reports/runs`,
        payload,
        requestConfig(context),
      ),

    reportRuns: (
      context: OperationsRequestContext,
      pageIndex = 0,
      pageSize = 25,
    ) =>
      HttpClient.get<RegulatoryReportRun[]>(
        `${OPERATIONS_SERVICE}/reports/runs`,
        requestConfig(context, { params: { pageIndex, pageSize } }),
      ),

    reconciliationRuns: (
      context: OperationsRequestContext,
      pageIndex = 0,
      pageSize = 25,
    ) =>
      HttpClient.get<ReconciliationRun[]>(
        `${OPERATIONS_SERVICE}/ops/reconciliation/eod/runs`,
        requestConfig(context, { params: { pageIndex, pageSize } }),
      ),

    downloadReconciliationRunReport: (
      context: OperationsRequestContext,
      runId: string,
    ) =>
      HttpClient.getDocument(
        `${OPERATIONS_SERVICE}/ops/reconciliation/eod/runs/${runId}/report`,
        requestConfig(context),
      ),

    reconciliationRunMismatches: (
      context: OperationsRequestContext,
      runId: string,
      params: { issueType: string; pageIndex?: number; pageSize?: number },
    ) =>
      HttpClient.get<ReconciliationMismatch[]>(
        `${OPERATIONS_SERVICE}/ops/reconciliation/eod/runs/${runId}/mismatches`,
        requestConfig(context, { params }),
      ),

    partyReconciliationRuns: (
      context: OperationsRequestContext,
      pageIndex = 0,
      pageSize = 25,
    ) =>
      HttpClient.get<ReconciliationRun[]>(
        `${OPERATIONS_SERVICE}/ops/reconciliation/parties/runs`,
        requestConfig(context, { params: { pageIndex, pageSize } }),
      ),

    downloadPartyReconciliationRunReport: (
      context: OperationsRequestContext,
      runId: string,
    ) =>
      HttpClient.getDocument(
        `${OPERATIONS_SERVICE}/ops/reconciliation/parties/runs/${runId}/report`,
        requestConfig(context),
      ),

    partyReconciliationRunMismatches: (
      context: OperationsRequestContext,
      runId: string,
      params: {
        issueType?: string;
        pageIndex?: number;
        pageSize?: number;
      } = {},
    ) =>
      HttpClient.get<PartyReconciliationMismatch[]>(
        `${OPERATIONS_SERVICE}/ops/reconciliation/parties/runs/${runId}/mismatches`,
        requestConfig(context, { params }),
      ),

    generateReport: (context: OperationsRequestContext, runId: string) =>
      HttpClient.post(
        `${OPERATIONS_SERVICE}/reports/runs/${runId}/generate`,
        context,
        requestConfig(context),
      ),

    approveReport: (
      context: OperationsRequestContext,
      runId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${OPERATIONS_SERVICE}/reports/runs/${runId}/approve`,
        payload,
        requestConfig(context),
      ),

    clientSnapshot: (
      context: OperationsRequestContext,
      clientId: string,
      accountId?: string,
    ) =>
      HttpClient.get(
        `${OPERATIONS_SERVICE}/clients/${clientId}/snapshot`,
        noCacheRequestConfig(context, { params: { accountId } }),
      ),

    searchClients: (
      context: OperationsRequestContext,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<ClientCareContextResponse>(
        `${OPERATIONS_SERVICE}/clients/search`,
        noCacheRequestConfig(context, { params }),
      ),

    clientDetails: (
      context: OperationsRequestContext,
      clientId: string,
      accountId?: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/details`,
        noCacheRequestConfig(context, { params: { accountId, ...params } }),
      ),

    clientAccounts: (
      context: OperationsRequestContext,
      clientId: string,
      accountId?: string,
    ) =>
      HttpClient.get<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/accounts`,
        noCacheRequestConfig(context, { params: { accountId } }),
      ),

    clientCases: (
      context: OperationsRequestContext,
      clientId: string,
      accountId?: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<PageResponse<OperationCase>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/cases`,
        noCacheRequestConfig(context, { params: { accountId, ...params } }),
      ),

    clientServiceRequests: (
      context: OperationsRequestContext,
      clientId: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<PageResponse<ClientServiceRequest>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/service-requests`,
        noCacheRequestConfig(context, { params }),
      ),

    clientDocuments: (
      context: OperationsRequestContext,
      clientId: string,
      accountId?: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<PageResponse<Record<string, unknown>>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/documents`,
        noCacheRequestConfig(context, { params: { accountId, ...params } }),
      ),

    clientHoldings: (
      context: OperationsRequestContext,
      clientId: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<PageResponse<AccountHoldingsView>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/holdings`,
        noCacheRequestConfig(context, { params }),
      ),

    clientSecureMessages: (
      context: OperationsRequestContext,
      clientId: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<PageResponse<Record<string, unknown>>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/secure-messages`,
        noCacheRequestConfig(context, { params }),
      ),

    clientAccountPerformanceHistory: (
      context: OperationsRequestContext,
      clientId: string,
      accountId: string,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/accounts/${accountId}/performance-history`,
        noCacheRequestConfig(context, { params }),
      ),

    clientCareTransactions: (
      context: OperationsRequestContext,
      clientId: string,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<PageResponse<Record<string, unknown>>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/transactions`,
        noCacheRequestConfig(context, { params }),
      ),

    cancelDeposit: (context: OperationsRequestContext, depositId: string) =>
      HttpClient.post<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/deposit/${depositId}/actions/cancel`,
        {},
        requestConfig(context),
      ),

    cancelOrder: (
      context: OperationsRequestContext,
      accountId: string,
      orderId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/account-management/accounts/${accountId}/orders/${orderId}/actions/cancel`,
        payload,
        requestConfig(context),
      ),

    updateAccountStatus: (
      context: OperationsRequestContext,
      accountId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch(
        `${OPERATIONS_SERVICE}/account-management/accounts/${accountId}/status`,
        payload,
        requestConfig(context),
      ),

    customerFlags: (context: OperationsRequestContext, clientId: string) =>
      HttpClient.get<CustomerProfileFlags>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/flags`,
        requestConfig(context),
      ),

    updateCustomerFlags: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.patch<CustomerProfileFlags>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/flags`,
        payload,
        requestConfig(context),
      ),

    accountManagementSearch: (
      context: OperationsRequestContext,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<ClientCareContextResponse>(
        `${OPERATIONS_SERVICE}/account-management/search`,
        requestConfig(context, { params }),
      ),

    accountRestrictions: (
      context: OperationsRequestContext,
      accountId: string,
    ) =>
      HttpClient.get<AccountRestrictionResponse>(
        `${OPERATIONS_SERVICE}/account-management/restrictions/${accountId}`,
        requestConfig(context),
      ),

    applyAccountRestrictions: (
      context: OperationsRequestContext,
      payload: AccountRestrictionPayload,
    ) =>
      HttpClient.post<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/account-management/restrictions`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflow: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}`,
        requestConfig(context),
      ),

    deceasedWorkflowTransition: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/transition`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflowRecordAuthority: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/record-authority`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflowRequestDocuments: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/request-documents`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflowRestrictAccounts: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/restrict-accounts`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflowRecordValuationReview: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/record-valuation-review`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflowSubmitSettlementInstruction: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/submit-settlement-instruction`,
        payload,
        requestConfig(context),
      ),

    deceasedWorkflowClose: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DeceasedCustomerWorkflow>(
        `${OPERATIONS_SERVICE}/deceased-customer-workflows/cases/${caseId}/close`,
        payload,
        requestConfig(context),
      ),

    emailMessages: (
      context: OperationsRequestContext,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<PageResponse<EmailItem>>(
        `${OPERATIONS_SERVICE}/emails`,
        requestConfig(context, { params }),
      ),

    sendEmail: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<EmailItem>(
        `${OPERATIONS_SERVICE}/emails`,
        payload,
        requestConfig(context),
      ),

    caseEmailMessages: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<OperationMessage[]>(
        `${OPERATIONS_SERVICE}/emails/cases/${caseId}/messages`,
        requestConfig(context),
      ),

    replyEmail: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<EmailItem>(
        `${OPERATIONS_SERVICE}/emails/cases/${caseId}/replies`,
        payload,
        requestConfig(context),
      ),

    documentRequests: (context: OperationsRequestContext, clientId: string) =>
      HttpClient.get(
        `${OPERATIONS_SERVICE}/clients/${clientId}/document-requests`,
        requestConfig(context),
      ),

    createServiceRequest: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<ClientServiceRequest>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/service-requests`,
        payload,
        requestConfig(context),
      ),

    cancelDocumentRequest: (
      context: OperationsRequestContext,
      clientId: string,
      documentRequestId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DocumentRequest>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/document-requests/${documentRequestId}/cancel`,
        payload,
        requestConfig(context),
      ),

    downloadClientDocument: (
      context: OperationsRequestContext,
      clientId: string,
      documentId: string,
    ) =>
      HttpClient.getDocument(
        `${OPERATIONS_SERVICE}/clients/${clientId}/documents/${documentId}/download`,
        requestConfig(context),
      ),



    servicingAccountSnapshot: (
      context: OperationsRequestContext,
      caseId: string,
      accountId: string,
    ) =>
      HttpClient.get<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/account-servicing/${caseId}/accounts/${accountId}/snapshot`,
        noCacheRequestConfig(context),
      ),

    listTransferOuts: (
      context: OperationsRequestContext,
      clientId: string,
      accountId: string,
    ) =>
      HttpClient.get<Record<string, unknown>[]>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/accounts/${accountId}/transfer-outs`,
        requestConfig(context),
      ),




    createMandate: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<MandateRecord>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/mandates`,
        payload,
        requestConfig(context),
      ),

    cancelMandate: (
      context: OperationsRequestContext,
      clientId: string,
      mandateId: string,
    ) =>
      HttpClient.post<MandateRecord>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/mandates/${mandateId}/cancel`,
        {},
        requestConfig(context),
      ),

    createDirectDebitPayment: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<Record<string, unknown>>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/direct-debit-payments`,
        payload,
        requestConfig(context),
      ),

    sendClientPortalMessage: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<OperationMessage>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/client-portal-messages`,
        payload,
        requestConfig(context),
      ),

    requestDocument: (
      context: OperationsRequestContext,
      clientId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<DocumentRequest>(
        `${OPERATIONS_SERVICE}/clients/${clientId}/document-requests`,
        payload,
        requestConfig(context),
      ),

    ingestInboundMessage: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${OPERATIONS_SERVICE}/api/inbound/messages`,
        payload,
        requestConfig(context),
      ),

    aiMetrics: (context: OperationsRequestContext) =>
      HttpClient.get(
        `${OPERATIONS_SERVICE}/api/ai/metrics`,
        requestConfig(context),
      ),

    quinKycCases: (
      context: OperationsRequestContext,
      payload: QuinSearchPayload,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/case/search`,
        payload,
        requestConfig(context),
      ),

    quinAllCases: (
      context: OperationsRequestContext,
      payload: QuinSearchPayload,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/case/all/search`,
        payload,
        requestConfig(context),
      ),

    quinAuthorisationKycSearch: (
      context: OperationsRequestContext,
      payload: QuinSearchPayload,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/authorisation/kyc/search`,
        payload,
        requestConfig(context),
      ),

    quinAuthorisationTmSearch: (
      context: OperationsRequestContext,
      payload: QuinSearchPayload,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/authorisation/tm/search`,
        payload,
        requestConfig(context),
      ),

    quinCaseCount: (context: OperationsRequestContext, tenantId: string) =>
      HttpClient.get(
        `${QUIN_CASE_SERVICE}/ops/case/count/${tenantId}`,
        requestConfig(context),
      ),

    quinScreeningChecks: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
    ) =>
      HttpClient.get(
        `${QUIN_CASE_SERVICE}/ops/checks/overall-screening/${clientId}/${caseId}`,
        requestConfig(context),
      ),

    quinCheckDetail: (
      context: OperationsRequestContext,
      checkName: string,
      clientId: string,
      caseId: string,
      entityId?: string,
    ) =>
      HttpClient.get(
        `${QUIN_CASE_SERVICE}/ops/checks/${checkName}/${clientId}/${caseId}`,
        requestConfig(context, { params: { entityId } }),
      ),

    quinRetriggerCheck: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/checks/re-trigger`,
        payload,
        requestConfig(context),
      ),

    quinOverrideCheck: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put(
        `${QUIN_CASE_SERVICE}/ops/updateKycCheckResponse/${clientId}/${caseId}`,
        payload,
        requestConfig(context),
      ),

    quinDocuments: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
    ) =>
      HttpClient.get(
        `${QUIN_CASE_SERVICE}/ops/document/${clientId}/${caseId}`,
        requestConfig(context),
      ),

    quinReviewDocuments: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
    ) =>
      HttpClient.get(
        `${QUIN_CASE_SERVICE}/ops/document/${clientId}/${caseId}/reviewDocuments`,
        requestConfig(context),
      ),

    quinAcknowledgeDocumentReview: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put(
        `${QUIN_CASE_SERVICE}/ops/document/${clientId}/${caseId}/acknowledgeDocumentReview`,
        payload,
        requestConfig(context),
      ),

    quinKycAnalystReview: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/review/kyc-analyst`,
        payload,
        requestConfig(context),
      ),

    quinSupervisorApproval: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/review/supervisor`,
        payload,
        requestConfig(context),
      ),

    quinTmCaseReview: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/tm/case/review/case-review`,
        payload,
        requestConfig(context),
      ),

    quinTmSupervisorApproval: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${QUIN_CASE_SERVICE}/ops/tm/case/review/supervisor-approval`,
        payload,
        requestConfig(context),
      ),

    qkvinCases: (context: OperationsRequestContext) =>
      HttpClient.get<QkvinCaseResponse[]>(
        `${QUIN_ADAPTER_SERVICE}/case/list`,
        requestConfig(context),
      ),

    qkvinCaseDetails: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<QkvinCaseResponse>(
        `${QUIN_ADAPTER_SERVICE}/case/details/${caseId}`,
        requestConfig(context),
      ),

    qkvinCaseWorkflow: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
    ) =>
      HttpClient.get<QkvinWorkflowResponse>(
        `${QUIN_ADAPTER_SERVICE}/case/workflow/${clientId}/${caseId}`,
        requestConfig(context),
      ),

    qkvinOverallScreeningResult: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
    ) =>
      HttpClient.get<QkvinOverallScreeningResult>(
        `${QUIN_ADAPTER_SERVICE}/case/screening-checks/${clientId}/${caseId}`,
        requestConfig(context),
      ),

    qkvinCheckDetails: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
      checkName: string,
      checkLabel?: string,
    ) =>
      HttpClient.get<QkvinCheckDetailResponse>(
        `${QUIN_ADAPTER_SERVICE}/case/${clientId}/${caseId}/checks/${checkName}`,
        requestConfig(context, { params: checkLabel ? { checkLabel } : {} }),
      ),

    qkvinOverrideCheck: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put<boolean>(
        `${QUIN_ADAPTER_SERVICE}/case/${clientId}/${caseId}/updateKycCheckResponse`,
        payload,
        requestConfig(context),
      ),

    qkvinReviewDocuments: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
    ) =>
      HttpClient.get<QkvinCheckDetailResponse>(
        `${QUIN_ADAPTER_SERVICE}/case/${clientId}/${caseId}/reviewDocuments`,
        requestConfig(context),
      ),

    qkvinAcknowledgeDocumentReview: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put<boolean>(
        `${QUIN_ADAPTER_SERVICE}/case/${clientId}/${caseId}/acknowledgeDocumentReview`,
        payload,
        requestConfig(context),
      ),

    qkvinDownloadDocument: (
      context: OperationsRequestContext,
      clientId: string,
      caseId: string,
      docId: string,
    ) =>
      HttpClient.getDocument(
        `${QUIN_ADAPTER_SERVICE}/case/${clientId}/${caseId}/document/${docId}/download`,
        requestConfig(context),
      ),

    qkvinKycAnalystReview: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put<QkvinKycAnalystReviewResponse>(
        `${QUIN_ADAPTER_SERVICE}/case/kyc-analyst-review`,
        payload,
        requestConfig(context),
      ),

    qkvinSupervisorApproval: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.put<boolean>(
        `${QUIN_ADAPTER_SERVICE}/case/supervisor-approval`,
        payload,
        requestConfig(context),
      ),

    amlCases: (
      context: OperationsRequestContext,
      params: Record<string, unknown>,
    ) =>
      HttpClient.get<PageResponse<AmlCaseSummary>>(
        `${AML_SERVICE}/cases`,
        requestConfig(context, { params }),
      ),

    amlRules: (
      context: OperationsRequestContext,
      params: Record<string, unknown> = {},
    ) =>
      HttpClient.get<AmlRuleConfig[]>(
        `${AML_SERVICE}/rules`,
        requestConfig(context, { params }),
      ),

    createAmlRule: (
      context: OperationsRequestContext,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<AmlRuleConfig>(
        `${AML_SERVICE}/rules`,
        payload,
        requestConfig(context),
      ),

    submitAmlRule: (
      context: OperationsRequestContext,
      ruleId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<AmlRuleConfig>(
        `${AML_SERVICE}/rules/${ruleId}/submit`,
        payload,
        requestConfig(context),
      ),

    approveAmlRule: (
      context: OperationsRequestContext,
      ruleId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<AmlRuleConfig>(
        `${AML_SERVICE}/rules/${ruleId}/approve`,
        payload,
        requestConfig(context),
      ),

    rejectAmlRule: (
      context: OperationsRequestContext,
      ruleId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<AmlRuleConfig>(
        `${AML_SERVICE}/rules/${ruleId}/reject`,
        payload,
        requestConfig(context),
      ),

    amlCaseWorkspace: (context: OperationsRequestContext, caseId: string) =>
      HttpClient.get<AmlCaseWorkspaceResponse>(
        `${AML_SERVICE}/cases/${caseId}/workspace`,
        requestConfig(context),
      ),

    assignAmlCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${AML_SERVICE}/cases/${caseId}/assign`,
        payload,
        requestConfig(context),
      ),

    decideAmlCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post(
        `${AML_SERVICE}/cases/${caseId}/decision`,
        payload,
        requestConfig(context),
      ),

    escalateAmlCase: (
      context: OperationsRequestContext,
      caseId: string,
      actorId: string,
    ) =>
      HttpClient.post(
        `${AML_SERVICE}/cases/${caseId}/escalate`,
        undefined,
        requestConfig(context, { params: { actorId } }),
      ),

    closeAmlCase: (
      context: OperationsRequestContext,
      caseId: string,
      actorId: string,
    ) =>
      HttpClient.post(
        `${AML_SERVICE}/cases/${caseId}/close`,
        undefined,
        requestConfig(context, { params: { actorId } }),
      ),

    restrictAmlCase: (
      context: OperationsRequestContext,
      caseId: string,
      payload: Record<string, unknown>,
    ) =>
      HttpClient.post<{ restrictionId: string }>(
        `${AML_SERVICE}/cases/${caseId}/restrictions`,
        payload,
        requestConfig(context),
      ),

    releaseAmlRestriction: (
      context: OperationsRequestContext,
      caseId: string,
      restrictionId: string,
      actorId: string,
    ) =>
      HttpClient.post(
        `${AML_SERVICE}/cases/${caseId}/restrictions/${restrictionId}/release`,
        undefined,
        requestConfig(context, { params: { actorId } }),
      ),

    draftAmlSar: (
      context: OperationsRequestContext,
      caseId: string,
      actorId: string,
    ) =>
      HttpClient.post<{ sarId: string }>(
        `${AML_SERVICE}/cases/${caseId}/sar/draft`,
        undefined,
        requestConfig(context, { params: { actorId } }),
      ),
  };
};
